import { useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  AlertCircle,
  ArrowLeft,
  Check,
  ChevronDown,
  ChevronUp,
  CreditCard,
  DollarSign,
  Inbox,
  Layers,
  Link2,
  Link as LinkIcon,
  Plus,
  RefreshCw,
  Search,
  Shield,
  Tag,
  TrendingUp,
  X,
} from "lucide-react";
import { Badge } from "@/components/Badge";
import {
  FormField,
  MoneyField,
  NumberField,
  DropdownField,
} from "@/components/FormField";
import { PrimaryButton } from "@/components/PrimaryButton";
import {
  calcCostPerCard,
  generateId,
  useApp,
  type Card,
  type CardCondition,
  type ProductStatus,
  type ProductType,
} from "@/context/AppContext";
import {
  generateBundles,
  type BundleSuggestion,
} from "@/features/bundleEngine";
import { buildDescriptionForProduct } from "@/features/bundleDescription";
import { enrichBundleSuggestions, rankBundles } from "@/features/bundleRanking";

const PRODUCT_TYPES: ProductType[] = [
  "100-card Random Lot",
  "50-card Themed Lot",
  "25-card Trainer Lot",
  "Holo/Reverse Bundle",
  "4x Playset",
  "Mystery Pack",
  "Starter/Kid Pack",
  "Premium Single",
  "Sealed Bundle",
  "Custom",
];

interface ProductCreateSearch {
  /** Comma-separated card ids to pre-select (deeplink from recommendations). */
  initialCardIds?: string;
  initialProductType?: ProductType;
}

export const Route = createFileRoute("/products/create")({
  validateSearch: (search: Record<string, unknown>): ProductCreateSearch => ({
    initialCardIds:
      typeof search.initialCardIds === "string" &&
      search.initialCardIds.length > 0
        ? search.initialCardIds
        : undefined,
    initialProductType:
      typeof search.initialProductType === "string" &&
      (PRODUCT_TYPES as string[]).includes(search.initialProductType)
        ? (search.initialProductType as ProductType)
        : undefined,
  }),
  component: ProductCreatePage,
});

const STATUSES: ProductStatus[] = [
  "Draft",
  "Ready to List",
  "Listed",
  "Sold",
  "Shipped",
];

const PICKER_CONDITIONS: (CardCondition | "All")[] = [
  "All",
  "NM",
  "LP",
  "MP",
  "HP",
  "Damaged",
];
const MARKET_TARGET_RATIO = 0.8;

function ProductCreatePage() {
  const navigate = useNavigate();
  const {
    lots,
    cards,
    addProductWithPackaging,
    storageBins,
    products,
    liveListings,
    sales,
    bundleSettings,
  } = useApp();

  const fieldRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const bindField = (key: string) => (el: HTMLDivElement | null) => {
    fieldRefs.current[key] = el;
  };
  const scrollToField = (key: string) => {
    const el = fieldRefs.current[key];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const { initialCardIds, initialProductType } = Route.useSearch();

  const [productType, setProductType] = useState<ProductType>(
    initialProductType ?? "100-card Random Lot",
  );
  const [name, setName] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [cardCount, setCardCount] = useState("");
  const [sourceLotId, setSourceLotId] = useState("");
  const [costBasis, setCostBasis] = useState("");
  const [costOverridden, setCostOverridden] = useState(false);
  const [packagingCost, setPackagingCost] = useState("");
  const [targetSalePrice, setTargetSalePrice] = useState("");
  const [priceOverridden, setPriceOverridden] = useState(false);
  const [listingTitle, setListingTitle] = useState("");
  const [titleLinked, setTitleLinked] = useState(true);
  const [description, setDescription] = useState("");
  const [storageLocation, setStorageLocation] = useState("");
  const [status, setStatus] = useState<ProductStatus>("Draft");
  const [saving, setSaving] = useState(false);
  const [validationError, setValidationError] = useState("");

  const [selectedCardIds, setSelectedCardIds] = useState<Set<string>>(
    () =>
      new Set(
        initialCardIds
          ? initialCardIds.split(",").filter((id) => id.length > 0)
          : [],
      ),
  );
  const [cardSearch, setCardSearch] = useState("");
  const [pickerCondition, setPickerCondition] = useState<CardCondition | "All">(
    "All",
  );
  const [pickerBin, setPickerBin] = useState<string>("All");
  const [pickerExpanded, setPickerExpanded] = useState(true);
  const [suggestionsExpanded, setSuggestionsExpanded] = useState(true);
  const [bundleIdeasExpanded, setBundleIdeasExpanded] = useState(true);
  const [bundleIdeasShowAll, setBundleIdeasShowAll] = useState(false);
  const [appliedStrategyId, setAppliedStrategyId] = useState<
    BundleSuggestion["strategyId"] | undefined
  >(undefined);

  const unlistedCards = useMemo(
    () => cards.filter((c) => c.listingStatus === "Unlisted"),
    [cards],
  );

  const suggestedCards = useMemo(
    () =>
      cards
        .filter(
          (c) =>
            c.listingStatus === "Unlisted" &&
            c.marketPrice > 0 &&
            c.marketPrice > c.costBasis &&
            c.quantity > 0,
        )
        .map((c) => ({
          card: c,
          gap: (c.marketPrice - c.costBasis) * c.quantity,
        }))
        .sort((a, b) => b.gap - a.gap)
        .slice(0, 5)
        .map((x) => x.card),
    [cards],
  );

  const rawBundles = useMemo(
    () => generateBundles(unlistedCards, bundleSettings),
    [unlistedCards, bundleSettings],
  );

  const enrichedBundles = useMemo(
    () =>
      enrichBundleSuggestions(rawBundles, { products, liveListings, sales }),
    [rawBundles, products, liveListings, sales],
  );

  const bundleIdeas = useMemo(
    () => rankBundles(enrichedBundles, bundleSettings.sortMode),
    [enrichedBundles, bundleSettings.sortMode],
  );

  // Reset bin filter if the selected bin is deleted/renamed.
  useEffect(() => {
    if (pickerBin === "All") return;
    if (!storageBins.some((b) => b.name === pickerBin)) setPickerBin("All");
  }, [storageBins, pickerBin]);

  const filteredCards = useMemo(() => {
    let result = unlistedCards;
    if (pickerCondition !== "All")
      result = result.filter((c) => c.condition === pickerCondition);
    if (pickerBin !== "All")
      result = result.filter((c) => c.storageLocation === pickerBin);
    if (cardSearch.trim()) {
      const q = cardSearch.toLowerCase();
      result = result.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          c.setName.toLowerCase().includes(q) ||
          c.rarity.toLowerCase().includes(q),
      );
    }
    return result;
  }, [unlistedCards, cardSearch, pickerCondition, pickerBin]);

  const lotMap = useMemo(
    () => Object.fromEntries(lots.map((l) => [l.id, l])),
    [lots],
  );

  const derivedValues = useMemo(() => {
    const selectedCards = cards.filter((c) => selectedCardIds.has(c.id));
    const totalQty = selectedCards.reduce((s, c) => s + c.quantity, 0);

    let derivedCost = 0;
    const lotQtyMap: Record<string, number> = {};
    for (const card of selectedCards) {
      const lot = card.sourceLotId ? lotMap[card.sourceLotId] : undefined;
      const cpc = lot ? calcCostPerCard(lot) : card.costBasis;
      derivedCost += cpc * card.quantity;
      if (card.sourceLotId) {
        lotQtyMap[card.sourceLotId] =
          (lotQtyMap[card.sourceLotId] ?? 0) + card.quantity;
      }
    }

    let dominantLotId = "";
    let maxQty = 0;
    for (const [lid, qty] of Object.entries(lotQtyMap)) {
      if (qty > maxQty) {
        maxQty = qty;
        dominantLotId = lid;
      }
    }

    const totalMarket = selectedCards.reduce(
      (s, c) => s + (c.marketPrice || 0) * c.quantity,
      0,
    );

    return { totalQty, derivedCost, dominantLotId, totalMarket };
  }, [selectedCardIds, cards, lotMap]);

  // Shared helper that recomputes derived field values for a given selection set.
  const recomputeFromSelection = (ids: Set<string>) => {
    const selectedCards = cards.filter((c) => ids.has(c.id));
    const totalQty = selectedCards.reduce((s, c) => s + c.quantity, 0);
    let derived = 0;
    const lotQtyMap: Record<string, number> = {};
    for (const c of selectedCards) {
      const lot = c.sourceLotId ? lotMap[c.sourceLotId] : undefined;
      const cpc = lot ? calcCostPerCard(lot) : c.costBasis;
      derived += cpc * c.quantity;
      if (c.sourceLotId) {
        lotQtyMap[c.sourceLotId] = (lotQtyMap[c.sourceLotId] ?? 0) + c.quantity;
      }
    }
    let dominantLotId = "";
    let maxQ = 0;
    for (const [lid, q] of Object.entries(lotQtyMap)) {
      if (q > maxQ) {
        maxQ = q;
        dominantLotId = lid;
      }
    }
    const totalMarket = selectedCards.reduce(
      (s, c) => s + (c.marketPrice || 0) * c.quantity,
      0,
    );
    setCardCount(totalQty > 0 ? String(totalQty) : "");
    if (!costOverridden) {
      setCostBasis(derived > 0 ? derived.toFixed(2) : "");
    }
    if (!priceOverridden) {
      if (totalMarket > 0) {
        setTargetSalePrice((totalMarket * MARKET_TARGET_RATIO).toFixed(2));
        if (validationError) setValidationError("");
      } else {
        setTargetSalePrice("");
      }
    }
    if (dominantLotId) setSourceLotId(dominantLotId);
  };

  const toggleCard = (card: Card) => {
    setSelectedCardIds((prev) => {
      const next = new Set(prev);
      if (next.has(card.id)) next.delete(card.id);
      else next.add(card.id);
      recomputeFromSelection(next);
      return next;
    });
  };

  const applyBundle = (suggestion: BundleSuggestion) => {
    const ids = new Set(selectedCardIds);
    for (const c of suggestion.cards) ids.add(c.id);
    setSelectedCardIds(ids);
    recomputeFromSelection(ids);
    setProductType(suggestion.suggestedProductType);
    setName(suggestion.suggestedTitle);
    setListingTitle(suggestion.suggestedTitle);
    setTitleLinked(true);
    setAppliedStrategyId(suggestion.strategyId);
  };

  const titleForProductType = (pt: ProductType, count: number): string => {
    const titles: Record<ProductType, string> = {
      "100-card Random Lot": `${count || 100}-Card Random Pokemon Lot - Great for Kids & Collectors`,
      "50-card Themed Lot": `${count || 50}-Card Themed Pokemon Lot - Mix of Rares & Commons`,
      "25-card Trainer Lot": `${count || 25}-Card Trainer/Supporter Lot - Ready to Play`,
      "Holo/Reverse Bundle": `${count || 10}-Card Holo & Reverse Holo Bundle - NM/LP Condition`,
      "4x Playset": `4x Playset - ${name || "Pokemon Card"} - ${status}`,
      "Mystery Pack": `Mystery Pack - ${count || 20} Surprise Cards - Guaranteed Rares`,
      "Starter/Kid Pack": `Starter Pokemon Pack - ${count || 50} Cards - Perfect for Beginners`,
      "Premium Single": name || "Premium Pokemon Single - NM Condition",
      "Sealed Bundle": "Sealed Pokemon Product Bundle",
      "Pre-built Deck": "60-Card Casual Pokemon Deck - Ready to Play",
      "Deck Core": `Pokemon Deck Core / Upgrade Kit - ${count || 20} Cards`,
      "Deck Upgrade Kit": "Pokemon Deck Upgrade Kit - Key Support & Staples",
      "Set Builder Lot": `Pokemon Set Builder Lot - ${count || 50} Cards`,
      "Character Theme Bundle": name || "Pokemon Character Theme Bundle",
      "Type Theme Bundle": name || "Pokemon Type Theme Bundle",
      "Trainer Staples Kit":
        "Pokemon Trainer Staples Kit - Deck Building Support",
      "Energy Kit": `Pokemon Energy Kit - ${count || 20} Energy Cards`,
      Custom: name || "Pokemon Card Lot",
      // TNK-112 shell — title-suggestion never runs from this code path
      // (Imported products are created server-side from an eBay title),
      // but the Record<ProductType, string> exhaustiveness check requires
      // a key here.
      Imported: name || "Imported eBay Listing",
    };
    return titles[pt] || "";
  };

  const cost = parseFloat(costBasis) || 0;
  const packaging = parseFloat(packagingCost) || 0;
  const salePrice = parseFloat(targetSalePrice) || 0;
  const totalCost = cost + packaging;
  const profit = salePrice - totalCost;
  const roi = totalCost > 0 ? (profit / totalCost) * 100 : 0;

  const autoTitle = () => {
    const count = parseInt(cardCount) || 0;
    const generated = titleForProductType(productType, count);
    setListingTitle(generated);
    setName(generated);
    setTitleLinked(true);
  };

  const handleSave = () => {
    if (!targetSalePrice) {
      setValidationError("Target sale price is required");
      scrollToField("targetSalePrice");
      return;
    }
    setValidationError("");
    setSaving(true);
    const productId = generateId("PROD");
    // TNK-113: addProductWithPackaging flips every card in cardIds to
    // listingStatus="In Package" + packagedProductId=this product. The
    // earlier call site used addProduct, which left the seed card loose
    // even when it landed in cardIds — exactly the operator-reported bug.
    addProductWithPackaging({
      id: productId,
      productType,
      name: name || productType,
      quantity: parseInt(quantity) || 1,
      cardCount: parseInt(cardCount) || 0,
      cardIds: Array.from(selectedCardIds),
      sourceLotId,
      costBasis: cost,
      packagingCost: packaging,
      targetSalePrice: salePrice,
      listingTitle,
      description,
      storageLocation,
      status,
      createdAt: new Date().toISOString(),
      bundleStrategyId: appliedStrategyId,
    });
    setSaving(false);
    navigate({ to: "/products/$id", params: { id: productId } });
  };

  return (
    <section className="flex h-full flex-col">
      <div className="border-b border-border pb-3">
        <Link
          to="/products"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Cancel
        </Link>
        <h1 className="mt-2 text-2xl font-bold">Build Product</h1>
      </div>

      <div className="flex-1 space-y-4 overflow-y-auto py-4">
        {/* Calc card */}
        <div className="grid grid-cols-2 gap-2 rounded-md bg-primary p-4 text-primary-foreground sm:grid-cols-4">
          <CalcItem
            label="Sale Price"
            value={`$${salePrice.toFixed(2)}`}
            color="text-secondary"
          />
          <CalcItem label="Total Cost" value={`$${totalCost.toFixed(2)}`} />
          <CalcItem
            label="Profit"
            value={`$${profit.toFixed(2)}`}
            color={profit >= 0 ? "text-emerald-400" : "text-red-400"}
          />
          <CalcItem label="ROI" value={`${roi.toFixed(0)}%`} />
        </div>

        <DropdownField
          label="Product Type"
          value={productType}
          options={PRODUCT_TYPES}
          onChange={(v) => setProductType(v as ProductType)}
        />
        <FormField
          label="Product Name / Label"
          value={name}
          onChangeText={(v) => {
            setName(v);
            if (titleLinked) setListingTitle(v);
          }}
          placeholder="Optional custom name"
        />
        <NumberField
          label="Quantity"
          value={quantity}
          onChangeText={setQuantity}
          placeholder="1"
        />
        <NumberField
          label="Card Count (per unit)"
          value={cardCount}
          onChangeText={setCardCount}
          placeholder="Auto-filled from selection"
        />

        {lots.length > 0 && (
          <div className="space-y-1.5">
            <label className="text-xs font-medium tracking-wide text-muted-foreground">
              Source Lot
            </label>
            <div className="flex flex-wrap gap-1.5">
              {lots.map((lot) => {
                const selected = sourceLotId === lot.id;
                return (
                  <button
                    key={lot.id}
                    type="button"
                    onClick={() => setSourceLotId(selected ? "" : lot.id)}
                    className={`flex flex-col items-start gap-0.5 rounded-md border px-3 py-1.5 text-left text-[11px] transition-colors ${
                      selected
                        ? "border-secondary bg-secondary/20 text-secondary"
                        : "border-border bg-card text-muted-foreground hover:border-secondary/40"
                    }`}
                  >
                    <span className="font-semibold">
                      {lot.seller || lot.id}
                    </span>
                    <span
                      className={
                        selected
                          ? "text-[10px] text-secondary/70"
                          : "text-[10px] text-muted-foreground/70"
                      }
                    >
                      {lot.id}
                    </span>
                  </button>
                );
              })}
            </div>
            {sourceLotId && (
              <button
                type="button"
                onClick={() => setSourceLotId("")}
                className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
              >
                <X className="h-3 w-3" /> Clear selection
              </button>
            )}
          </div>
        )}

        {/* Cards picker — collapsible */}
        <button
          type="button"
          onClick={() => setPickerExpanded((v) => !v)}
          className={`flex w-full items-center justify-between gap-3 rounded-md border bg-card px-3 py-2.5 text-left ${
            selectedCardIds.size > 0 ? "border-secondary" : "border-border"
          }`}
        >
          <div className="flex flex-wrap items-center gap-2">
            <CreditCard
              className={`h-4 w-4 ${
                selectedCardIds.size > 0
                  ? "text-secondary"
                  : "text-muted-foreground"
              }`}
            />
            <span
              className={`text-sm font-semibold ${
                selectedCardIds.size > 0
                  ? "text-foreground"
                  : "text-muted-foreground"
              }`}
            >
              Select Cards
            </span>
            {selectedCardIds.size > 0 && (
              <span className="rounded-md bg-secondary/20 px-2 py-0.5 text-[11px] font-medium text-secondary">
                {selectedCardIds.size} selected · qty {derivedValues.totalQty} ·
                ${derivedValues.derivedCost.toFixed(2)} cost
              </span>
            )}
          </div>
          {pickerExpanded ? (
            <ChevronUp className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          )}
        </button>

        {pickerExpanded && (
          <div className="rounded-md border border-border bg-card p-3">
            {unlistedCards.length === 0 ? (
              <div className="flex flex-col items-center gap-2 py-6 text-center">
                <Inbox className="h-5 w-5 text-muted-foreground" />
                <p className="text-xs text-muted-foreground">
                  No unlisted cards in inventory
                </p>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2 rounded-md bg-muted px-3 py-2">
                  <Search className="h-4 w-4 text-muted-foreground" />
                  <input
                    type="search"
                    value={cardSearch}
                    onChange={(e) => setCardSearch(e.target.value)}
                    placeholder="Search by name, set, rarity..."
                    className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
                    autoComplete="off"
                  />
                  {cardSearch.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setCardSearch("")}
                      aria-label="Clear search"
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>

                <div className="mt-2 flex flex-wrap gap-1 pb-1">
                  {PICKER_CONDITIONS.map((c) => {
                    const active = pickerCondition === c;
                    return (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setPickerCondition(c)}
                        className={`shrink-0 rounded-full px-3 py-1 text-[11px] font-medium transition-colors ${
                          active
                            ? "bg-primary text-primary-foreground"
                            : "bg-muted text-muted-foreground hover:bg-muted/80"
                        }`}
                      >
                        {c}
                      </button>
                    );
                  })}
                </div>

                {storageBins.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1 pb-1">
                    <button
                      type="button"
                      onClick={() => setPickerBin("All")}
                      className={`shrink-0 rounded-full px-3 py-1 text-[11px] font-medium transition-colors ${
                        pickerBin === "All"
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted text-muted-foreground hover:bg-muted/80"
                      }`}
                    >
                      All bins
                    </button>
                    {storageBins.map((b) => {
                      const active = pickerBin === b.name;
                      return (
                        <button
                          key={b.id}
                          type="button"
                          onClick={() => setPickerBin(b.name)}
                          className={`shrink-0 rounded-full px-3 py-1 text-[11px] font-medium transition-colors ${
                            active
                              ? "bg-primary text-primary-foreground"
                              : "bg-muted text-muted-foreground hover:bg-muted/80"
                          }`}
                        >
                          {b.name}
                        </button>
                      );
                    })}
                  </div>
                )}

                {filteredCards.length === 0 ? (
                  <div className="flex flex-col items-center gap-2 py-6 text-center">
                    <p className="text-xs text-muted-foreground">
                      No cards match the current filters
                    </p>
                  </div>
                ) : (
                  <ul className="mt-2 max-h-80 space-y-0 overflow-y-auto">
                    {filteredCards.map((card) => {
                      const isSelected = selectedCardIds.has(card.id);
                      const lot = card.sourceLotId
                        ? lotMap[card.sourceLotId]
                        : undefined;
                      const cpc = lot ? calcCostPerCard(lot) : card.costBasis;
                      const rowCost = cpc * card.quantity;
                      return (
                        <li
                          key={card.id}
                          className={`border-b border-border last:border-b-0 ${
                            isSelected ? "bg-secondary/10" : ""
                          }`}
                        >
                          <button
                            type="button"
                            onClick={() => toggleCard(card)}
                            className="flex w-full items-center gap-3 px-2 py-2 text-left"
                          >
                            <span
                              className={`flex h-4 w-4 items-center justify-center rounded border ${
                                isSelected
                                  ? "border-secondary bg-secondary text-primary"
                                  : "border-border bg-transparent"
                              }`}
                            >
                              {isSelected && <Check className="h-3 w-3" />}
                            </span>
                            <div className="min-w-0 flex-1">
                              <div className="truncate text-sm text-foreground">
                                {card.name}
                              </div>
                              <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                                <span className="text-[11px] text-muted-foreground">
                                  {card.setName} · #{card.cardNumber}
                                </span>
                                <Badge
                                  label={card.condition}
                                  variant={
                                    card.condition === "NM"
                                      ? "success"
                                      : card.condition === "Damaged"
                                        ? "danger"
                                        : "muted"
                                  }
                                />
                                <Badge label={card.rarity} variant="muted" />
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-sm font-semibold text-foreground">
                                ${rowCost.toFixed(2)}
                              </div>
                              {card.marketPrice > 0 && (
                                <div className="text-[11px] text-secondary">
                                  Mkt $
                                  {(card.marketPrice * card.quantity).toFixed(
                                    2,
                                  )}
                                </div>
                              )}
                              <div className="text-[11px] text-muted-foreground">
                                {lot ? `${cpc.toFixed(2)}/card` : "basis"} · ×
                                {card.quantity}
                              </div>
                            </div>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </>
            )}
          </div>
        )}

        {/* Suggested cards */}
        {suggestedCards.length > 0 && (
          <div className="rounded-md border border-secondary/55 bg-card">
            <button
              type="button"
              onClick={() => setSuggestionsExpanded((v) => !v)}
              className="flex w-full items-center justify-between gap-2 p-3 text-left"
            >
              <div className="flex items-center gap-2">
                <TrendingUp className="h-3.5 w-3.5 text-secondary" />
                <span className="text-sm font-semibold text-foreground">
                  Suggested Cards
                </span>
                <span className="rounded-md bg-secondary/20 px-1.5 py-0.5 text-[11px] font-medium text-secondary">
                  {suggestedCards.length}
                </span>
              </div>
              {suggestionsExpanded ? (
                <ChevronUp className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              )}
            </button>

            {suggestionsExpanded && (
              <ul className="border-t border-border">
                {suggestedCards.map((card) => {
                  const isSelected = selectedCardIds.has(card.id);
                  const gap =
                    (card.marketPrice - card.costBasis) * card.quantity;
                  return (
                    <li
                      key={card.id}
                      className={`border-b border-border last:border-b-0 ${
                        isSelected ? "bg-secondary/10" : ""
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() => toggleCard(card)}
                        className="flex w-full items-center gap-2 px-3 py-2 text-left"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm text-foreground">
                            {card.name}
                          </div>
                          <div className="text-[11px] text-muted-foreground">
                            {card.setName} · +${gap.toFixed(2)} potential
                          </div>
                        </div>
                        <span
                          className={`flex h-7 w-7 items-center justify-center rounded-md text-white ${
                            isSelected ? "bg-emerald-500" : "bg-secondary"
                          }`}
                        >
                          {isSelected ? (
                            <Check className="h-3.5 w-3.5" />
                          ) : (
                            <Plus className="h-3.5 w-3.5" />
                          )}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}

        {/* Bundle ideas */}
        {bundleSettings.globalEnabled && (
          <div className="rounded-md border border-primary/55 bg-card">
            <button
              type="button"
              onClick={() => setBundleIdeasExpanded((v) => !v)}
              className="flex w-full items-center justify-between gap-2 p-3 text-left"
            >
              <div className="flex items-center gap-2">
                <Layers className="h-3.5 w-3.5 text-primary" />
                <span className="text-sm font-semibold text-foreground">
                  Bundle Ideas
                </span>
                {bundleIdeas.length > 0 && (
                  <span className="rounded-md bg-primary/20 px-1.5 py-0.5 text-[11px] font-medium text-primary">
                    {bundleIdeas.length}
                  </span>
                )}
              </div>
              {bundleIdeasExpanded ? (
                <ChevronUp className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              )}
            </button>

            {bundleIdeasExpanded && bundleIdeas.length === 0 && (
              <div className="flex flex-col items-center gap-2 border-t border-border p-5 text-center">
                <Layers className="h-5 w-5 text-muted-foreground" />
                <p className="text-xs text-muted-foreground">
                  No bundles detected yet. Add more unlisted cards to surface
                  bundle ideas.
                </p>
              </div>
            )}

            {bundleIdeasExpanded &&
              (bundleIdeasShowAll ? bundleIdeas : bundleIdeas.slice(0, 5)).map(
                (idea) => {
                  const alreadyApplied = idea.cards.every((c) =>
                    selectedCardIds.has(c.id),
                  );
                  const gameLabel =
                    idea.game === "Pokemon"
                      ? "Pokémon"
                      : idea.game === "Magic"
                        ? "MTG"
                        : idea.game === "Yu-Gi-Oh"
                          ? "Yu-Gi-Oh!"
                          : idea.game;
                  const profitPositive = idea.expectedProfit >= 0;
                  return (
                    <div
                      key={idea.id}
                      className={`flex items-start gap-2 border-t border-border p-3 ${
                        alreadyApplied ? "bg-primary/5" : ""
                      }`}
                    >
                      <div className="min-w-0 flex-1 space-y-1">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className="rounded-md bg-secondary/20 px-1.5 py-0.5 text-[10px] font-semibold text-secondary">
                            {gameLabel}
                          </span>
                          <span className="rounded-md bg-primary/15 px-1.5 py-0.5 text-[10px] font-semibold text-primary">
                            {idea.bundleType}
                          </span>
                          <span className="rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                            {idea.confidenceScore}%
                          </span>
                        </div>
                        <div className="truncate text-sm font-semibold text-foreground">
                          {idea.label}
                        </div>
                        <div className="text-[11px] text-muted-foreground">
                          {idea.cardCount} cards · $
                          {idea.estimatedMarketValue.toFixed(2)} market
                        </div>
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span
                            className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-medium ${
                              profitPositive
                                ? "bg-emerald-500/15 text-emerald-600"
                                : "bg-destructive/15 text-destructive"
                            }`}
                          >
                            <DollarSign className="h-2.5 w-2.5" />
                            Est profit ${idea.expectedProfit.toFixed(2)}
                          </span>
                          <span className="inline-flex items-center gap-1 rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                            <TrendingUp className="h-2.5 w-2.5" />
                            Speed:{" "}
                            {idea.sellSpeed === "Unknown"
                              ? "Unknown"
                              : idea.sellSpeed}
                          </span>
                          <span className="inline-flex items-center gap-1 rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                            <Shield className="h-2.5 w-2.5" />
                            Conf: {idea.confidence}
                            {idea.historicalSampleSize > 0
                              ? ` (n=${idea.historicalSampleSize})`
                              : " · no history"}
                          </span>
                          <span className="inline-flex items-center gap-1 rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                            <Tag className="h-2.5 w-2.5" />
                            {idea.suggestedProductType}
                          </span>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => applyBundle(idea)}
                        className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-[11px] font-semibold text-white ${
                          alreadyApplied ? "bg-emerald-500" : "bg-primary"
                        }`}
                      >
                        {alreadyApplied ? (
                          <Check className="h-3 w-3" />
                        ) : (
                          <Plus className="h-3 w-3" />
                        )}
                        {alreadyApplied ? "Applied" : "Use"}
                      </button>
                    </div>
                  );
                },
              )}

            {bundleIdeasExpanded && bundleIdeas.length > 5 && (
              <button
                type="button"
                onClick={() => setBundleIdeasShowAll((v) => !v)}
                className="flex w-full items-center justify-center gap-1.5 border-t border-border p-2.5 text-[11px] font-medium text-primary"
              >
                {bundleIdeasShowAll ? (
                  <ChevronUp className="h-3 w-3" />
                ) : (
                  <ChevronDown className="h-3 w-3" />
                )}
                {bundleIdeasShowAll
                  ? "Show top 5 only"
                  : `Show all ${bundleIdeas.length} ideas`}
              </button>
            )}
          </div>
        )}

        {/* Cost basis */}
        <div>
          <MoneyField
            label={
              selectedCardIds.size > 0 && !costOverridden
                ? `Cost Basis (derived from ${selectedCardIds.size} card${
                    selectedCardIds.size !== 1 ? "s" : ""
                  })`
                : "Cost Basis"
            }
            value={costBasis}
            onChangeText={(v) => {
              setCostBasis(v);
              setCostOverridden(true);
            }}
          />
          {selectedCardIds.size > 0 && costOverridden && (
            <button
              type="button"
              onClick={() => {
                setCostBasis(
                  derivedValues.derivedCost > 0
                    ? derivedValues.derivedCost.toFixed(2)
                    : "",
                );
                setCostOverridden(false);
              }}
              className="inline-flex items-center gap-1 text-[11px] text-secondary hover:underline"
            >
              <RefreshCw className="h-3 w-3" />
              Reset to derived ${derivedValues.derivedCost.toFixed(2)}
            </button>
          )}
        </div>

        <MoneyField
          label="Packaging Cost"
          value={packagingCost}
          onChangeText={setPackagingCost}
        />

        <div ref={bindField("targetSalePrice")}>
          <MoneyField
            label={
              selectedCardIds.size > 0 &&
              !priceOverridden &&
              derivedValues.totalMarket > 0
                ? `Target Sale Price (${Math.round(MARKET_TARGET_RATIO * 100)}% of market)`
                : "Target Sale Price"
            }
            value={targetSalePrice}
            onChangeText={(v) => {
              setTargetSalePrice(v);
              setPriceOverridden(true);
              if (v) setValidationError("");
            }}
            required
            error={validationError || undefined}
          />
        </div>
        {selectedCardIds.size > 0 &&
          !priceOverridden &&
          derivedValues.totalMarket > 0 && (
            <p className="text-[11px] text-muted-foreground">
              auto: {Math.round(MARKET_TARGET_RATIO * 100)}% of $
              {derivedValues.totalMarket.toFixed(2)} market value
            </p>
          )}
        {selectedCardIds.size > 0 &&
          priceOverridden &&
          derivedValues.totalMarket > 0 && (
            <button
              type="button"
              onClick={() => {
                setTargetSalePrice(
                  (derivedValues.totalMarket * MARKET_TARGET_RATIO).toFixed(2),
                );
                setPriceOverridden(false);
                setValidationError("");
              }}
              className="inline-flex items-center gap-1 text-[11px] text-secondary hover:underline"
            >
              <RefreshCw className="h-3 w-3" />
              Reset to auto $
              {(derivedValues.totalMarket * MARKET_TARGET_RATIO).toFixed(2)} (
              {Math.round(MARKET_TARGET_RATIO * 100)}% of market)
            </button>
          )}
        {validationError && (
          <div className="flex items-center gap-1.5 text-[11px] text-destructive">
            <AlertCircle className="h-3 w-3" />
            {validationError}
          </div>
        )}

        <FormField
          label="Storage Location"
          value={storageLocation}
          onChangeText={setStorageLocation}
          placeholder="BOX-A, SHELF-2, etc."
        />
        <DropdownField
          label="Status"
          value={status}
          options={STATUSES}
          onChange={(v) => setStatus(v as ProductStatus)}
        />

        {/* Listing section */}
        <div className="flex items-center justify-between pt-1">
          <span className="text-sm font-semibold text-foreground">Listing</span>
          <button
            type="button"
            onClick={() =>
              setTitleLinked((v) => {
                if (!v && name) setListingTitle(name);
                return !v;
              })
            }
            aria-label={
              titleLinked
                ? "Title linked to product name. Tap to unlink."
                : "Title unlinked. Tap to link to product name."
            }
            className="inline-flex items-center gap-1 text-[11px]"
          >
            {titleLinked ? (
              <LinkIcon className="h-3.5 w-3.5 text-secondary" />
            ) : (
              <Link2 className="h-3.5 w-3.5 text-muted-foreground" />
            )}
            <span
              className={
                titleLinked
                  ? "font-medium text-secondary"
                  : "font-medium text-muted-foreground"
              }
            >
              {titleLinked ? "Linked" : "Unlinked"}
            </span>
          </button>
        </div>

        <PrimaryButton
          label="Auto-Generate Title"
          onClick={autoTitle}
          variant="secondary"
        />

        <FormField
          label="Listing Title"
          value={listingTitle}
          onChangeText={(v) => {
            setListingTitle(v);
            setTitleLinked(false);
          }}
          placeholder="eBay / Facebook title..."
        />
        <FormField
          label="Description"
          value={description}
          onChangeText={setDescription}
          placeholder="Auto-generated from selected cards, or paste a {{template}} here."
          multiline
        />
        {selectedCardIds.size > 0 && (
          <button
            type="button"
            onClick={() => {
              const selectedCards = cards.filter((c) =>
                selectedCardIds.has(c.id),
              );
              const generated = buildDescriptionForProduct(
                {
                  productType,
                  bundleStrategyId: appliedStrategyId,
                  targetSalePrice: parseFloat(targetSalePrice) || 0,
                },
                selectedCards,
              );
              setDescription(generated.plainText);
            }}
            className="inline-flex items-center gap-1 rounded-md bg-muted px-2.5 py-1.5 text-[11px] font-semibold text-muted-foreground hover:text-foreground"
          >
            <RefreshCw className="h-3 w-3" />
            Generate description from cards
          </button>
        )}

        <PrimaryButton
          label="Create Product"
          onClick={handleSave}
          loading={saving}
        />
      </div>
    </section>
  );
}

function CalcItem({
  value,
  label,
  color,
}: {
  value: string;
  label: string;
  color?: string;
}) {
  return (
    <div className="flex flex-col items-center gap-0.5">
      <div
        className={`text-lg font-bold ${color ?? "text-primary-foreground"}`}
      >
        {value}
      </div>
      <div className="text-[10px] uppercase tracking-wide opacity-75">
        {label}
      </div>
    </div>
  );
}
